import { AxiosResponse } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { FilePreview } from 'models/File';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import { MINUTE } from 'data/global';

export const FILE_PREVIEW_URL_QUERY_KEY = 'file-preview-url';
const PREVIEW_CACHE_TIME = 30 * MINUTE; // 30 min

export const getFilePreviewUrlQueryKey = (fileId: string, scope?: string) => [
  FILE_PREVIEW_URL_QUERY_KEY,
  { fileId, scope },
];

type UseFilePreviewUrlQueryProps = {
  fileId: string;
  /** owning resource (session / note / lead) — keeps ids from different resources apart */
  scope?: string;
  fetchPreview: (fileId: string) => Promise<AxiosResponse<FilePreview>>;
  enabled?: boolean;
};

/**
 * Caches the short-lived signed url of a previewable file so re-opening it (or arrowing
 * back to it) doesn't hit the preview endpoint again. Mirrors `useFileBufferPreviewQuery`,
 * but takes the request as a callback so it works for every `{ url, type }` preview
 * endpoint (chat files, lead / deal notes, nda proof files).
 */
export default function useFilePreviewUrlQuery({
  fileId,
  scope,
  fetchPreview,
  enabled = true,
}: UseFilePreviewUrlQueryProps) {
  const getMixpanelProps = useGetMixpanelProps();

  return useQuery<FilePreview>({
    queryKey: getFilePreviewUrlQueryKey(fileId, scope),
    queryFn: async () => {
      const { data } = await fetchPreview(fileId);

      return data;
    },
    onError: (error) =>
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(error)) as ErrorViewedProps,
      ),
    enabled: enabled && !!fileId,
    staleTime: PREVIEW_CACHE_TIME,
    cacheTime: PREVIEW_CACHE_TIME,
  });
}
